import {
  derivationApi,
  type PairSimilarProblems,
  type PairSimilarProblemsBody,
  type WorkbookDerivation,
  type WorksheetDerivation,
} from '@mathflat/domain/Derivation'
import {
  makeWorksheetApi,
  type OneClickWorksheetFilterBody,
  type WeakConceptWorksheetFilterBody,
  type WeakPeriodWorksheetFilterBody,
  type WeakWorksheetWorksheetFilterBody,
  type WrongWorksheetType,
} from '@mathflat/domain/MakeWorksheet'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
import { MakeProblem, MakeProblemColl } from 'src/dto'
import { WorksheetDerivationResponse } from 'src/dto/Derivation'
import CheckableMap from 'src/models/CheckableMap'

import type {
  ByOneClick,
  ByWeakConceptChip,
  ByWorksheetChapter,
  ByWorksheetDateRange,
  DerivationByWorkbookPage,
  DerivationByWorkbookProblem,
} from '@/dto/Derivation/derivationParams'
import { WorkbookDerivationResponse } from '@/dto/Derivation/WorkbookDerivationResponse'
import type {
  ArithmeticChapterFilterParams,
  ChapterFilterParams,
  LeveledChapterFilterParams,
} from '@/dto/Worksheet/MakeWorksheetByChapterOption'
import type { DerivationWorksheetByStudentConceptChip } from '@/service/derivation/(filter)/(by)/derivationWorksheetByStudentConceptChip.type'
import type { DerivationWorksheetByWrongWorksheet } from '@/service/derivation/(filter)/(by)/derivationWorksheetByWrongWorksheet.type'
import { createLatestRequestSignal } from '@/utils/axios/latestRequestSignal'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

// 오답학습지 필터(단원별·기간별·학습지별) 요청은 단원유형별 필터 조건에서 서버가 받는 필드만 고른다. 서버는 dev 에서
// 모르는 필드가 섞이면 요청을 거부하므로(FAIL_ON_UNKNOWN_PROPERTIES), 단원유형별에만 있는 제외 유형·나의DB 포함·
// 출제 제외 기준·source 를 싣지 않는다. 2026-10-13 백엔드 develop(cc5f60e17) worksheet/controller/request/
// CreateWorksheetFilterBy{WeakConcept,DateRange,WeakWorksheet}Request.kt 로 확인.
const toWeakConceptFilterBody = (
  p: LeveledChapterFilterParams,
): WeakConceptWorksheetFilterBody => ({
  filterId: p.filterId,
  title: p.title,
  conceptIdList: p.conceptIdList,
  selectedConceptIdList: p.selectedConceptIdList,
  selectedLittleChapterIdList: p.selectedLittleChapterIdList,
  problemCount: p.problemCount,
  level: p.level,
  levelWeight: p.levelWeight,
  problemFilterType: p.problemFilterType,
  practiceTest: p.practiceTest,
  onlyAutoScorable: p.onlyAutoScorable,
  excludePrevious: p.excludePrevious ?? false,
  excludeOOC: p.excludeOOC,
  equalityLevel: p.equalityLevel,
  minRate: p.minRate,
  maxRate: p.maxRate,
  problemList: p.problemList,
})

const toWeakWorksheetFilterBody = (
  p: ChapterFilterParams | ArithmeticChapterFilterParams,
): WeakWorksheetWorksheetFilterBody => ({
  filterId: p.filterId,
  title: p.title,
  conceptIdList: p.conceptIdList,
  selectedConceptIdList: p.selectedConceptIdList,
  selectedLittleChapterIdList: p.selectedLittleChapterIdList,
  problemCount: p.problemCount,
  level: p.level,
  levelWeight: p.levelWeight,
  problemFilterType: p.problemFilterType,
  practiceTest: p.practiceTest,
  arithmeticOnly: p.arithmeticOnly,
  arithmeticLevel: p.arithmeticLevel,
  onlyAutoScorable: p.onlyAutoScorable,
  excludePrevious: p.excludePrevious ?? false,
  excludeOOC: p.excludeOOC,
  equalityLevel: p.equalityLevel,
  minRate: p.minRate,
  maxRate: p.maxRate,
  problemList: p.problemList,
})

class DerivationController implements Controller {
  // maxios 의 useAbortController 처럼 조건을 바꿔 다시 조회하면 앞선 요청을 버린다
  private latestSignal = {
    worksheetProblem: createLatestRequestSignal('GET/derivation/worksheet/problem'),
    casProblem: createLatestRequestSignal('GET/derivation/cas/problem'),
  }

  adapter = {
    worksheetDerivation: makeAdapter({
      from(response: WorksheetDerivation): WorksheetDerivationResponse {
        return new WorksheetDerivationResponse(response)
      },
    }),
    workbookDerivation: makeAdapter({
      from(response: WorkbookDerivation): WorkbookDerivationResponse {
        return new WorkbookDerivationResponse(response)
      },
    }),
    pairSimilarProblem: makeAdapter({
      from(
        problem: PairSimilarProblems['problem'],
        favorite: PairSimilarProblems['pairProblemList'][number]['favorite'],
        tagTop:
          | PairSimilarProblems['pairProblemList'][number]['tagTop']
          | PairSimilarProblems['similarProblemList'][number]['tagTop'],
        curriculum: PairSimilarProblems['curriculum'],
      ) {
        return new MakeProblem({
          ...problem,
          rate: problem.problemSummary.answerRate,
          tagTop,
          // 서버는 기준 문제의 후보에만 즐겨찾기 여부를 채운다
          favorite: favorite ?? false,
          defaultOrder: null,
          sequence: curriculum.sequence,
          conceptId: curriculum.conceptId ?? EMPTY_NUMBER_ID,
          conceptName: curriculum.conceptName ?? '',
          littleChapterName: curriculum.littleChapterName,
          middleChapterName: curriculum.middleChapterName,
        })
      },
    }),
  }

  // n차 학습지 - 교재 문항
  fetchByWorkbookProblem = async (params: DerivationByWorkbookProblem) => {
    const data = await derivationApi.getWorkbookProblemDerivation(params, {
      meta: { mp_event_name: 'T43-08_교재문제_쌍둥이유사검색&문제별오답학습지_문제 조회_API' },
    })
    return this.adapter.workbookDerivation.from(data)
  }

  // n차 학습지 - 교재 페이지
  fetchByWorkbookPage = async (params: DerivationByWorkbookPage) => {
    const data = await derivationApi.getWorkbookPageDerivation(params, {
      meta: { mp_event_name: 'T43-07_교재페이지별오답학습지문제조회_API' },
    })
    return this.adapter.workbookDerivation.from(data)
  }

  // 유형분석 학습지
  fetchByWeakConceptChip = async (params: ByWeakConceptChip) => {
    const data = await derivationApi.getConceptChipDerivation(params, {
      meta: { mp_event_name: 'T46-03_유형분석_학습지만들기_API' },
    })
    return this.adapter.worksheetDerivation.from(data)
  }

  wrongWorksheet = {
    filterResponse: {
      fetchByWeakWorksheetFilter: async (
        filter:
          | DerivationWorksheetByWrongWorksheet.ByWeakWorksheetFilter.ByConcept
          | DerivationWorksheetByWrongWorksheet.ByWeakWorksheetFilter.ByProblem,
      ) => {
        if (filter.wrongWorksheetType === 'WRONG_CONCEPT_WORKSHEET') {
          if (filter.levelType !== 'NORMAL') {
            filter.preferPairOverSimilar = undefined
          }
        } else {
          if (filter.problemRequestType === 'SAME') {
            filter.excludePrevious = false
          }
        }

        // 화면 조건은 학생 목록 생략을 허용하지만 서버는 빈 목록을 거부한다(생략과 같은 요청이 나간다)
        const data = await derivationApi.getWorksheetProblemDerivation(
          { ...filter, studentIdList: filter.studentIdList ?? [] },
          {
            meta: { mp_event_name: 'T42-09_학습지오답학습지문제조회_API' },
            signal: this.latestSignal.worksheetProblem({ useAbortController: true }),
          },
        )

        return this.adapter.worksheetDerivation.from(data)
      },
      fetchByCasProblem: async (filter: DerivationWorksheetByWrongWorksheet.ByCasFilter) => {
        if (filter.wrongWorksheetType === 'WRONG_CONCEPT_WORKSHEET') {
          if (filter.levelType !== 'NORMAL') {
            filter.preferPairOverSimilar = undefined
          }
        } else {
          if (filter.problemRequestType === 'SAME') {
            filter.excludePrevious = false
          }
        }

        const data = await derivationApi.getCasProblemDerivation(
          { ...filter, studentIdList: filter.studentIdList ?? [] },
          { signal: this.latestSignal.casProblem({ useAbortController: true }) },
        )

        return this.adapter.worksheetDerivation.from(data)
      },
    },
  }

  achievement = {
    filterResponse: {
      byStudent: async (
        params:
          | DerivationWorksheetByStudentConceptChip.Filter.ByConcept
          | DerivationWorksheetByStudentConceptChip.Filter.ByProblem,
      ) => {
        const data = await derivationApi.getConceptChipDerivation(params, {
          meta: { mp_event_name: 'T46-03_유형분석_학습지만들기_API' },
        })
        return this.adapter.worksheetDerivation.from(data)
      },
    },
  }

  // n차 학습지 - 단원
  fetchByWorksheetChapter = async <
    T extends WrongWorksheetType,
    U = T extends 'WRONG_CONCEPT_WORKSHEET' ? '유형수' | '정답률' : never,
  >(
    params: ByWorksheetChapter<T, U>,
  ) => {
    const data = await derivationApi.getWeakConceptDerivation(params, {
      meta: { mp_event_name: 'T42-07_단원별취약유형학습지문제조회_API' },
    })

    return this.adapter.worksheetDerivation.from(data)
  }
  // n차 학습지 - 날짜
  fetchByWorksheetDateRange = async (params: ByWorksheetDateRange) => {
    const data = await derivationApi.getDateRangeDerivation(params, {
      meta: { mp_event_name: 'T42-08_기간별오답학습지문제조회_API' },
    })

    return this.adapter.worksheetDerivation.from(data)
  }

  // n차 학습지 - 원클릭
  fetchByOneClick = async (params: ByOneClick) => {
    const data = await derivationApi.getOneClickDerivation(params, {
      meta: { mp_event_name: 'T99-01_학습지만들기_원클릭복습학습지_문제조회_API' },
    })
    return this.adapter.worksheetDerivation.from(data)
  }

  fetchFilterIdByWorksheet = async (data: ChapterFilterParams | ArithmeticChapterFilterParams) =>
    await makeWorksheetApi.createWeakWorksheetWorksheetFilter(toWeakWorksheetFilterBody(data), {
      meta: { mp_event_name: 'T42-11_개별오답학습지필터_필터생성변경시마다_API' },
    })

  fetchFilterIdByConcept = async (data: LeveledChapterFilterParams) =>
    await makeWorksheetApi.createWeakConceptWorksheetFilter(toWeakConceptFilterBody(data), {
      meta: { mp_event_name: 'T42-07_단원기간별_단원별_필터생성변경시마다_API' },
    })

  fetchFilterIdByPeriod = async (
    data: LeveledChapterFilterParams & Pick<WeakPeriodWorksheetFilterBody, 'startDate' | 'endDate'>,
  ) =>
    await makeWorksheetApi.createWeakPeriodWorksheetFilter(
      { ...toWeakConceptFilterBody(data), startDate: data.startDate, endDate: data.endDate },
      { meta: { mp_event_name: 'T42-08_단원기간별_기간별_필터생성변경시마다_API' } },
    )
  // 쌍둥이&유사문제 문제조회 : 백엔드 도메인따라가는 것이지만.. 원래는 problem도메인이고, 추후 바뀔 예정이라함..
  /**
   * @deprecated
   * 리뉴얼된 교재에서는 절대 쓰지 말것
   */
  async _fetchPairSimilarProblemList(
    id: PairSimilarProblems['problem']['id'],
    params: PairSimilarProblemsBody,
  ) {
    const data = await derivationApi.getPairSimilarProblems(id, params, {
      mp_event_name: 'T35-09_학습지만들기2_쌍둥이유사조회_API',
    })
    const similar = data.similarProblemList.map(({ problem, favorite, tagTop }) =>
      this.adapter.pairSimilarProblem.from(problem, favorite, tagTop, data.curriculum),
    )
    const pair = data.pairProblemList.map(({ problem, favorite, tagTop }) => {
      return this.adapter.pairSimilarProblem.from(problem, favorite, tagTop, data.curriculum)
    })

    return {
      similarProblemColl: new MakeProblemColl(CheckableMap.fromArr(similar, 'id')),
      pairProblemColl: new MakeProblemColl(CheckableMap.fromArr(pair, 'id')),
      curriculum: data.curriculum,
    }
  }

  fetchFilterIdByOneClick = async (data: OneClickWorksheetFilterBody) =>
    await makeWorksheetApi.createOneClickWorksheetFilter(data, {
      meta: { mp_event_name: 'T99-01_학습지만들기_원클릭복습학습지_필터생성변경시마다_API' },
    })
}

export const derivationController = new DerivationController()
